<template>
  <app-page :title="title" :icon="icon" :class="cssClass">
    <template #header>
      <div class="d-flex align-items-center">
        <divisions-dropdown v-if="showDivisionsDropdown" />
        <b-button
          v-if="portalUpdatesJan2026Enabled"
          variant="primary"
          :class="showDivisionsDropdown ? 'ml-3' : ''"
          :disabled="isGenerating || !canDownloadComputed"
          title="Download"
          @click="handleDownloadClick"
        >
          <b-spinner v-if="isGenerating" small />
          <font-awesome-icon v-else :icon="['fad', 'download']" class="mr-2" />Download
        </b-button>
      </div>
    </template>
    <template #custom-content>
      <b-col ref="contentRef">
        <analytics-nav />
        <slot />
      </b-col>
      <subsection-download-modal
        v-if="hasSectionSpecs"
        :sections="downloadSections"
        :get-page-extras="getSheetConfig"
        :visible="showModal"
        @hidden="showModal = false"
      />
    </template>
  </app-page>
</template>

<script setup>
import { computed, ref } from 'vue';
import AppPage from '@/components/AppPage.vue';
import SubsectionDownloadModal from '@/components/SubsectionDownloadModal.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import useSubsectionDownload from '@/composables/useSubsectionDownload';
import AnalyticsNav from '@/pages/analyticsHomepage/components/AnalyticsNav.vue';
import DivisionsDropdown from '@/pages/analyticsHomepage/components/DivisionsDropdown.vue';

const props = defineProps({
  title: { type: String, required: true },
  icon: { type: String, required: true },
  cssClass: { type: String, default: '' },
  defaultLabel: { type: String, required: true },
  showDivisionsDropdown: { type: Boolean, default: true },
  inlineKpis: { type: Boolean, default: true },
  canDownload: { type: Function, default: null },
  getSheetConfig: { type: Function, default: null },
  /**
   * Section specs for the multi-section download modal. Each spec:
   *   { key, label, ref?, [inlineKpis], [hasData], [getExportTable],
   *     [getContainerEl], [getPdfData] }
   * When provided, the Download button opens the modal instead of generating
   * a single-sheet PDF.
   *
   * Most specs are one ref per section: the ref'd component should expose:
   *   - `hasData` (required): boolean — whether the section is selectable.
   *     The section's root element must also self-hide when empty (e.g.
   *     `<section v-if="loading || hasData">`) so unselectable sections
   *     don't render a blank card on the page.
   *   - `getExportTable` (required for XLSX/CSV): `() => { headers, rows }`.
   *     Sections that omit this stay PDF-only in the modal.
   *   - `getPdfData` (optional): `() => SheetExtras` — PDF-specific extras
   *     such as `trendingBrands` or `topKpis`. See `SheetExtras` typedef in
   *     `useSubsectionDownload`.
   *
   * For pages that carve one component into multiple sections, specs can
   * pass explicit callbacks (`hasData`, `getExportTable`, `getContainerEl`,
   * `getPdfData`) that override the ref-based lookup.
   */
  sectionSpecs: { type: Array, default: () => [] },
});

const contentRef = ref(null);
const showModal = ref(false);
const { portalUpdatesJan2026Enabled } = useFeatureFlags();
const { isGenerating, downloadSheet } = useSubsectionDownload();

const hasSectionSpecs = computed(() => props.sectionSpecs.length > 0);

// Collapse both spec shapes (ref-based and callback-based) into a single
// uniform descriptor so downstream computeds don't branch per field.
function normalizeSpec(spec) {
  const child = () => spec.ref?.value;
  const childExportTable = typeof child()?.getExportTable === 'function';
  return {
    key: spec.key,
    label: spec.label,
    inlineKpis: spec.inlineKpis ?? true,
    hasData: () => (typeof spec.hasData === 'function' ? !!spec.hasData() : !!child()?.hasData),
    // Only set when the spec actually exposes one, so the modal can filter
    // sections out of the Excel/CSV checklist (sections without tabular data
    // stay PDF-only).
    getExportTable:
      typeof spec.getExportTable === 'function'
        ? spec.getExportTable
        : childExportTable
        ? () => child()?.getExportTable?.()
        : null,
    getContainerEl: () => (typeof spec.getContainerEl === 'function' ? spec.getContainerEl() : child()?.$el),
    getPdfData: () => (typeof spec.getPdfData === 'function' ? spec.getPdfData() || {} : child()?.getPdfData?.() || {}),
  };
}

const normalizedSpecs = computed(() => props.sectionSpecs.map(normalizeSpec));

const canDownloadComputed = computed(() => {
  if (props.canDownload) {
    return props.canDownload();
  }
  if (hasSectionSpecs.value) {
    return normalizedSpecs.value.some(s => s.hasData());
  }
  return true;
});

const downloadSections = computed(() =>
  normalizedSpecs.value
    .filter(s => s.hasData())
    .map(s => ({
      key: s.key,
      label: s.label,
      inlineKpis: s.inlineKpis,
      getContainerEl: s.getContainerEl,
      ...(s.getExportTable ? { getExportTable: s.getExportTable } : {}),
      getPdfData: s.getPdfData,
    })),
);

async function handleDownloadClick() {
  if (hasSectionSpecs.value) {
    showModal.value = true;
    return;
  }
  await downloadSheet({
    label: props.defaultLabel,
    getContainerEl: () => contentRef.value?.$el || contentRef.value,
    inlineKpis: props.inlineKpis,
    getPdfData: props.getSheetConfig || undefined,
  });
}
</script>
